import React from 'react';
import { X } from 'lucide-react';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'destructive' | 'default' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  icon?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  loading = false,
  icon
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'destructive':
        return {
          confirmBg: 'bg-red-600 hover:bg-red-700 text-white',
          textColor: 'text-red-600',
          iconColor: 'text-red-600'
        };
      case 'warning':
        return {
          confirmBg: 'bg-orange-500 hover:bg-orange-600 text-white',
          textColor: 'text-orange-500',
          iconColor: 'text-orange-500'
        };
      case 'info':
        return {
          confirmBg: 'bg-blue-500 hover:bg-blue-600 text-white',
          textColor: 'text-blue-500',
          iconColor: 'text-blue-500'
        };
      default:
        return {
          confirmBg: 'bg-primary hover:bg-primary/90 text-primary-foreground',
          textColor: 'text-green-600',
          iconColor: 'text-green-600'
        };
    }
  };

  const { confirmBg, textColor, iconColor } = getVariantStyles();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <div className="relative bg-background border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
        {/* Close button */}
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        {/* Content */}
        <div className="flex flex-col space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            {icon && <div className={iconColor}>{icon}</div>}
            <h3 className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </h3>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-muted-foreground">
              {description}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <button
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 ${confirmBg}`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook for easy usage
export const useConfirmDialog = () => {
  const [state, setState] = React.useState<{
    open: boolean;
    title: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'destructive' | 'default' | 'warning' | 'info';
    onConfirm?: () => void | Promise<void>;
    icon?: React.ReactNode;
  }>({
    open: false,
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
    onConfirm: undefined,
    icon: undefined
  });

  const confirm = React.useCallback((options: Omit<typeof state, 'open'>) => {
    setState({
      ...options,
      open: true
    });
  }, []);

  const close = React.useCallback(() => {
    setState(prev => ({ ...prev, open: false }));
  }, []);

  const ConfirmDialogComponent = React.useCallback(() => (
    <ConfirmDialog
      open={state.open}
      onOpenChange={close}
      title={state.title}
      description={state.description}
      confirmText={state.confirmText}
      cancelText={state.cancelText}
      variant={state.variant}
      onConfirm={async () => {
        await state.onConfirm?.();
        close();
      }}
      icon={state.icon}
    />
  ), [state, close]);

  return {
    confirm,
    close,
    ConfirmDialog: ConfirmDialogComponent
  };
};

// Convenience functions for common use cases
export const createDeleteConfirm = (
  itemName: string,
  onConfirm: () => void | Promise<void>,
  description?: string
) => ({
  title: `Delete ${itemName}?`,
  description: description || `Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be undone.`,
  confirmText: 'Delete',
  cancelText: 'Cancel',
  variant: 'destructive' as const,
  onConfirm
});

export const createDeactivateConfirm = (
  itemName: string,
  isActive: boolean,
  onConfirm: () => void | Promise<void>
) => ({
  title: `${isActive ? 'Deactivate' : 'Activate'} ${itemName}?`,
  description: `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this ${itemName.toLowerCase()}?`,
  confirmText: isActive ? 'Deactivate' : 'Activate',
  cancelText: 'Cancel',
  variant: 'warning' as const,
  onConfirm
});

export default ConfirmDialog;